import React, { FormEvent, useEffect, useState } from 'react'
import moment from 'moment';
import { CustomCellRendererProps } from 'ag-grid-react';
import type { ColDef, RowValueChangedEvent } from "ag-grid-community";

import { CustomModal } from '../../modals/CustomModal';
import DataTable from '../../reusables/DataTable';
import { SVGIcon } from '../../wrappers/SVGIcon';

import { groups } from '../../../data/dummy_categories';
import { Icons } from '../../../data/Assets';

import useUpdateCategory from '../../../hooks/category/useUpdateCategory';
import useCreateCategory from '../../../hooks/category/useCreateCategory';
import useDeleteCategory from '../../../hooks/category/useDeleteCategory';
import { useGetScores } from '../../../hooks/score/useGetScores';
import useDeleteDraw from '../../../hooks/draw/useDeleteDraw';
import useGetDrawsByCategoryId from '../../../hooks/draw/useGetDrawsByCategoryId';
import useSaveDraw from '../../../hooks/draw/useSaveDraw';
import useDuplicateCategory from '../../../hooks/category/useDuplicateCategory';

import { ICategory } from '../../../types/category';
import { IDraw } from '../../../types/draw';

interface ICategoryProps {
    category: ICategory;
    currOption: number[];
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isDelete: boolean;
    isCreateDuplicate: boolean;
    disableDelete: boolean;
    setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
    setDuplicateModalText: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
    setCreateDuplicate: React.Dispatch<React.SetStateAction<boolean>>;
    setCreateDuplicateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Category = ({ category, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, disableDelete, isCreateDuplicate, setIsResetNew, setDeleteModalMessage, setCreateDuplicate, setCreateDuplicateModal, setDuplicateModalText }: ICategoryProps) => {

    const [title, setTitle] = useState<string>(category.id ? category.name : 'New Category');

    const [currentCategory, setCurrentCategory] = useState<ICategory>(category);

    const [draws, setDraws] = useState<IDraw[]>([]);
    const [isOpenCustomModal, setIsOpenCustomModal] = useState<boolean>(false); //eslint-disable-line
    const [customModalArgs, setCustomModalArgs] = useState<{ id: number | undefined; index: number } | null>(null); //eslint-disable-line
    const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
    const [apiError, setApiError] = useState<string | null>(null); //eslint-disable-line

    const blankDraw: IDraw = {
        id: null,
        draw_number: 0,
        draw_details: {
            gov_urL: null,
            ministerial_instruction: null,
            draw_names: []
        },
        category_id: currentCategory.id ?? 0,
        draw_date: moment.utc(new Date()).format('YYYY-MM-DD'),
        lowest_points: 0,
        invited: 0
    };

    useEffect(() => {
        setCurrentCategory(category);
        setTitle(category.id ? category.name : 'New Category');
    }, [category]);

    const { mutate: updateCategory } = useUpdateCategory({ setIsProcessRunning, setIsServiceError: setApiError });
    const { mutate: createCategory } = useCreateCategory({ setIsProcessRunning, setIsServiceError: setApiError });
    const { mutate: deleteCategory } = useDeleteCategory();

    const { data: scores } = useGetScores();

    const { data: drawsByCategory } = useGetDrawsByCategoryId(currentCategory.id);
    const { mutate: saveDraw } = useSaveDraw();
    const { mutate: deleteDraw } = useDeleteDraw({ setIsProcessRunning, setIsServiceError: setApiError });
    const { mutate: duplicateCategory } = useDuplicateCategory();

    useEffect(() => {
        if (!currentCategory.id)
            setDraws([]);
    }, [currentCategory.id]); //eslint-disable-line

    useEffect(() => {
        if (drawsByCategory) {
            const orgDraws = drawsByCategory.map(draw => ({ ...draw, draw_date: moment.utc(new Date(draw.draw_date)).format('YYYY-MM-DD') }));
            setDraws(orgDraws);
        }
    }, [drawsByCategory]); //eslint-disable-line

    useEffect(() => {
        if (!category.id) return;
        if (isCreateDuplicate && category?.id) {
            duplicateCategory({
                category_id: category.id,
                setIsProcessing: setIsProcessRunning,
                setApiError: setApiError,
                successCallback: () => {
                    setCurrOption([currOption[0]]);
                    setCreateDuplicate(false)
                }
            });
        }
    }, [category?.id, isCreateDuplicate]) //eslint-disable-line

    useEffect(() => {
        if (isDelete) {
            setCurrOption([0]);
            setIsResetNew(true);
            if (!category.id) return;
            deleteCategory(category.id);
            setIsDelete(false);
        }
    }, [isDelete]); //eslint-disable-line

    const onDrawRowValueChange = (event: RowValueChangedEvent) => {
        const data = event.data;

        setDraws(draws.map((draw, index) => index === event.node.rowIndex ? {
            ...draw,
            draw_number: data.draw_number,
            draw_details: draw.draw_details ? {
                gov_urL: draw?.draw_details?.gov_urL,
                ministerial_instruction: data?.draw_details?.ministerial_instruction,
                draw_names: draw?.draw_details?.draw_names
            } : draw.draw_details,
            draw_date: data.draw_date,
            lowest_points: data.lowest_points,
            invited: data.invited
        } : draw));

        setIsOpenCustomModal(false);
    }

    const onSaveDrawData = (props: CustomCellRendererProps<any, any, any>) => {
        props.api.stopEditing();
        const data = props.data;

        const draw = draws.find((draw, i) => i === props.node.rowIndex);

        const payload: IDraw = {
            id: data.id,
            draw_number: data.draw_number,
            draw_details: {
                gov_urL: draw?.draw_details?.gov_urL ?? null,
                ministerial_instruction: data.draw_details.ministerial_instruction,
                draw_names: draw?.draw_details?.draw_names ?? []
            },
            category_id: currentCategory.id ?? 0,
            draw_date: moment.utc(data.draw_date).format('YYYY-MM-DD'),
            lowest_points: data.lowest_points,
            invited: data.invited
        }

        saveDraw({
            data: payload,
            setIsProcessing: setIsProcessRunning,
            setApiError: setApiError
        });
    }

    const onDeleteDrawCell = ({ id, index }: { id: number | undefined; index: number }) => {
        if (id) deleteDraw(id);
        else {
            setDraws(draws.filter((_, i) => i !== index));
        }
    }

    if (!currentCategory) {
        return <p>Loading...</p>
    }

    const handleSave = (e: FormEvent) => {
        e.preventDefault();
        if (!category.id) {
            createCategory(currentCategory);
        } else {
            updateCategory(currentCategory);
        }
        setTitle(currentCategory.name || 'New Category');
    }

    const handleDelete = () => {
        if (!category.id) {
            setIsResetNew(true);
            setCurrOption([0]);
        } else {
            setIsOpenDeleteModal(true);
            setDeleteModalMessage({ title: 'Delete Category', subtitle: 'This will delete all the programs, components, functions and parameters associated with the category. Are you sure you want to delete?' });
        }
    }

    const addEmptyDrawField = () => {
        setDraws([blankDraw, ...draws]);
    }

    const drawColumns: ColDef[] = [
        { headerName: 'id', field: 'id', hide: true },
        // { headerName: 'Sr.No.', field: 'serial_no', valueGetter: 'node.rowIndex + 1', flex: 0.4 },
        { headerName: 'Draw Number', field: 'draw_number', editable: true, flex: 1 },
        // { headerName: 'GOV URL', field: 'draw_details.gov_urL', type: 'url', editable: true, flex: 1 },
        { headerName: 'Ministerial Instruction', field: 'draw_details.ministerial_instruction', editable: true, flex: 1 },
        {
            headerName: 'Draw Date', field: "draw_date", flex: 1, cellRenderer: (props: CustomCellRendererProps) =>
                <input style={{ border: 0 }} type='date' value={props.value} onChange={(e) =>
                    setDraws(draws.map((draw, i) => i === props.node.rowIndex ? { ...draw, draw_date: e.target.value } : draw))
                } />
        },
        { headerName: 'Lowest Points', field: "lowest_points", editable: true, flex: 1 },
        { headerName: 'Invited', field: "invited", editable: true, flex: 1 },
        {
            headerName: 'Actions', field: 'actions', cellRenderer: (props: CustomCellRendererProps) => {
                return (
                    <div className='action__field'>
                        <button className='imm_admin__btn_save' onClick={() => onSaveDrawData(props)}>
                            <SVGIcon src={Icons.DocSaveFill} color='primary' />
                        </button>
                        <button className='imm_admin__btn_delete' onClick={() => { setIsOpenCustomModal(true); setCustomModalArgs({ id: props.data.id, index: props.node.rowIndex ?? 0 }) }}>
                            <SVGIcon src={Icons.TrashFill} color='error' />
                        </button>
                    </div>
                );
            }, cellStyle: { cursor: 'pointer', 'textAlign': 'center' }, sortable: false, flex: 0.5
        }
    ];

    const openDuplicateModal = () => {
        setDuplicateModalText({ title: 'Create Duplicate', subtitle: 'This will create a duplicate of the category. Are you sure you want to continue?' });
        setCreateDuplicateModal(true);
    };

    return (
        <>
            {customModalArgs ? <CustomModal title='Delete Draw' subtitle='Are you sure you want to delete this draw?' isOpenCustomModal={isOpenCustomModal} setIsOpenCustomModal={setIsOpenCustomModal} args={customModalArgs} onAccept={onDeleteDrawCell} /> : null}
            <div className='imm_admin__content'>
                <div className='content_head'>
                    <h3>Category - {title}</h3>
                    {category.id && <button type='button' onClick={openDuplicateModal}>Create Duplicate</button>}
                </div>
                {apiError && <p className='color_danger'>{apiError}</p>}

                <form onSubmit={handleSave} className='content_form'>
                    <div className='field'>
                        <label htmlFor='category'>Category <span className='field__required'>*</span></label>
                        <input id='category' name='category' placeholder='Enter Category' value={currentCategory.name || ''} onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })} />
                    </div>
                    {scores ? (
                        <div className='field'>
                            <label htmlFor='score'>Score</label>
                            <select id='score' name='score' value={currentCategory.score_id || -1} onChange={(e) => setCurrentCategory({ ...currentCategory, score_id: Number(e.target.value) !== -1 ? Number(e.target.value) : undefined })}>
                                <option value={-1} >No Score</option>
                                {scores.map(score => {
                                    return <option key={score.id} value={score.id}>{score.name}</option>
                                })}
                            </select>
                        </div>

                    ) : <p>Loading Scores...</p>}

                    {groups ? (
                        <div className='field'>
                            <label htmlFor='group'>Group <span className='field__required'>*</span></label>
                            <select id='group' name='group' value={currentCategory.group || groups[0].value} onChange={(e) => setCurrentCategory({ ...currentCategory, group: e.target.value })}>
                                {groups.map((group, key) => {
                                    return <option key={key} value={group.key}>{group.value}</option>
                                })}
                            </select>
                        </div>
                    ) : <p>Loading Group...</p>}
                    <div className='field'>
                        <label htmlFor='description'>Description</label>
                        <textarea id='description' name='description' placeholder='Enter Description' value={currentCategory.description || ""} onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}></textarea>
                    </div>

                    {currentCategory?.id ? (
                        <>
                            <div className='field'>
                                <div className='field__label_btn'>
                                    <label htmlFor='draws'>Draws</label>
                                    <button type='button' onClick={addEmptyDrawField}>Add Draw</button>
                                </div>
                                {draws.length ? <DataTable columnDefs={drawColumns} data={draws} onRowValueChanged={onDrawRowValueChange} /> : <h6 className='text_center color_disabled'>No Draws</h6>}
                            </div>
                        </>
                    ) :
                        <p>Create the category first to start adding draws</p>
                    }

                    <div className='actions'>
                        <button disabled={disableDelete} className='delete_btn' type="button" onClick={handleDelete}>{!category.id ? 'Cancel' : 'Delete'}</button>
                        <button className='save_btn' type='submit'>{isProcessRunning ? <i className='imm_admin__process_loader' /> : !category.id ? 'Create' : 'Save'}</button>
                    </div>
                </form>
            </div>
        </>
    );
}
export default Category;